import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import AppShell from "@/components/AppShell";
import PortalCompare from "@/components/PortalCompare";
import CityAutocomplete from "@/components/CityAutocomplete";
import EstimateReportPage from "@/components/EstimateReportPage";
import DismissibleIntro from "@/components/DismissibleIntro";
import ReopenGuidesBtn from "@/components/ReopenGuidesBtn";
import { exportSinglePageAsPdf } from "@/lib/staticExport";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { CurrencyEur, House, Calculator, Info, ArrowLeft, CaretDown, Link as LinkIcon, Copy, WhatsappLogo, Users, Sparkle, FilePdf } from "@phosphor-icons/react";

const STATE_OPTIONS = [
  { value: "nuovo",            label: "Nuovo / Ristrutturato" },
  { value: "ottimo",           label: "Ottimo stato" },
  { value: "buono",            label: "Buono stato" },
  { value: "normale",          label: "Normale" },
  { value: "scarso",           label: "Scarso" },
  { value: "da_ristrutturare", label: "Da ristrutturare" },
];

const ZONE_OPTIONS = [
  { value: "centrale",     label: "Centrale" },
  { value: "semicentrale", label: "Semicentrale" },
  { value: "periferica",   label: "Periferica" },
  { value: "extraurbana",  label: "Extraurbana" },
];

const TYPE_OPTIONS = [
  { value: "appartamento", label: "Appartamento" },
  { value: "attico",       label: "Attico" },
  { value: "villa",        label: "Villa" },
  { value: "villetta",     label: "Villetta a schiera" },
  { value: "loft",         label: "Loft" },
  { value: "monolocale",   label: "Monolocale" },
  { value: "ufficio",      label: "Ufficio" },
  { value: "negozio",      label: "Negozio" },
];

const ENERGY_OPTIONS = [
  { value: "",   label: "— non specificata —" },
  { value: "A4", label: "A4 (eccellente)" },
  { value: "A3", label: "A3" },
  { value: "A2", label: "A2" },
  { value: "A1", label: "A1" },
  { value: "B",  label: "B" },
  { value: "C",  label: "C" },
  { value: "D",  label: "D" },
  { value: "E",  label: "E" },
  { value: "F",  label: "F" },
  { value: "G",  label: "G (peggiore)" },
];

const WINDOWS_OPTIONS = [
  { value: "",              label: "— non specificato —" },
  { value: "nuovi",         label: "Nuovi / doppi vetri" },
  { value: "discreti",      label: "Discreti" },
  { value: "da_sostituire", label: "Da sostituire" },
];

const EXPOSURE_OPTIONS = [
  { value: "",          label: "— non specificata —" },
  { value: "sud",       label: "Sud" },
  { value: "doppia",    label: "Doppia esposizione" },
  { value: "est_ovest", label: "Est / Ovest" },
  { value: "nord",      label: "Nord" },
];

const VIEW_OPTIONS = [
  { value: "",           label: "— non specificata —" },
  { value: "panoramica", label: "Panoramica" },
  { value: "aperta",     label: "Aperta" },
  { value: "interna",    label: "Interna" },
  { value: "cortile",    label: "Su cortile" },
];

const fmtEUR = (n) => `€ ${Math.round(n || 0).toLocaleString("it-IT")}`;
const pct = (x) => `${x > 0 ? "+" : ""}${Math.round(x * 100)}%`;

const CONFIDENCE_BADGE = {
  alta:  { label: "Confidenza alta",  cls: "bg-[#7C8B6E]/10 text-[#5C6B4E] border-[#7C8B6E]/30" },
  media: { label: "Confidenza media", cls: "bg-amber-50 text-amber-800 border-amber-200" },
  bassa: { label: "Confidenza bassa", cls: "bg-[#F8E5C8] text-[#8A4530] border-[#B8624A]/30" },
};


/**
 * StimaCopyEditor — iter v28 r358
 * Sotto-card del funnel Pronto-Stima. Permette all'agente di personalizzare
 * 3 testi mostrati ai proprietari sul link pubblico /stima/{code}:
 *   • Titolone in cima alla pagina
 *   • Sottotitolo descrittivo
 *   • Testo del bottone CTA "Ti chiamo io" / "Voglio essere ricontattato"
 * Tutti opzionali — se vuoti, fallback al copy default Pronto.
 * Le modifiche si salvano via PUT /api/profile/agency (PATCH semantics).
 */
function StimaCopyEditor() {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [copy, setCopy] = useState({
    stima_headline: "",
    stima_subtitle: "",
    stima_cta_text: "",
  });

  useEffect(() => {
    api.get("/auth/me").then((r) => {
      const u = r.data || {};
      setCopy({
        stima_headline: u.stima_headline || "",
        stima_subtitle: u.stima_subtitle || "",
        stima_cta_text: u.stima_cta_text || "",
      });
    }).catch(() => {});
  }, []);

  const save = async () => {
    setBusy(true);
    try {
      // Stringa vuota → backend la ignora (preserva valore esistente).
      // Per CANCELLARE un campo bisognerebbe passare null esplicito;
      // qui per semplicità trattiamo "" come "torna al default Pronto"
      // mandando null.
      const payload = {
        stima_headline: copy.stima_headline.trim() || null,
        stima_subtitle: copy.stima_subtitle.trim() || null,
        stima_cta_text: copy.stima_cta_text.trim() || null,
      };
      await api.put("/profile/agency", payload);
      toast.success("Personalizzazione salvata · ricarica il link per vedere il nuovo testo");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Salvataggio non riuscito");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mt-4 border-t border-white/10 pt-4" data-testid="stima-copy-editor">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="text-[11.5px] text-white/80 hover:text-white inline-flex items-center gap-1.5 underline-offset-2 hover:underline"
        data-testid="stima-copy-editor-toggle"
      >
        <Sparkle size={11} weight="fill" />
        {open ? "Nascondi" : "Personalizza"} il testo del link · {open ? "−" : "+"}
      </button>
      {open && (
        <div className="mt-3 space-y-3">
          <div>
            <label className="text-[10.5px] uppercase tracking-[0.14em] text-white/60 font-bold">Titolo</label>
            <input
              type="text"
              value={copy.stima_headline}
              onChange={(e) => setCopy((c) => ({ ...c, stima_headline: e.target.value }))}
              placeholder="Quanto vale la tua casa? | Lo scopri in 30 secondi."
              maxLength={200}
              className="w-full mt-1 px-3 py-2 rounded-lg text-[13px] bg-black/30 border border-white/15 text-white placeholder-white/40 focus:outline-none focus:border-[#A8B89A]"
              data-testid="stima-copy-headline"
            />
            <div className="text-[10px] text-white/40 mt-1">
              Usa <code className="text-white/60">|</code> per andare a capo (la seconda parte sarà in corsivo verde).
            </div>
          </div>
          <div>
            <label className="text-[10.5px] uppercase tracking-[0.14em] text-white/60 font-bold">Sottotitolo</label>
            <textarea
              rows={2}
              value={copy.stima_subtitle}
              onChange={(e) => setCopy((c) => ({ ...c, stima_subtitle: e.target.value }))}
              placeholder="Solo 4 dati sulla casa. Niente nome, niente telefono per vedere la stima."
              maxLength={300}
              className="w-full mt-1 px-3 py-2 rounded-lg text-[13px] bg-black/30 border border-white/15 text-white placeholder-white/40 focus:outline-none focus:border-[#A8B89A] resize-none"
              data-testid="stima-copy-subtitle"
            />
          </div>
          <div>
            <label className="text-[10.5px] uppercase tracking-[0.14em] text-white/60 font-bold">Testo bottone CTA</label>
            <input
              type="text"
              value={copy.stima_cta_text}
              onChange={(e) => setCopy((c) => ({ ...c, stima_cta_text: e.target.value }))}
              placeholder="Ti chiamo io"
              maxLength={120}
              className="w-full mt-1 px-3 py-2 rounded-lg text-[13px] bg-black/30 border border-white/15 text-white placeholder-white/40 focus:outline-none focus:border-[#A8B89A]"
              data-testid="stima-copy-cta"
            />
          </div>
          <button
            type="button"
            onClick={save}
            disabled={busy}
            className="w-full bg-white text-[#1C1C1A] py-2.5 rounded-lg text-[13px] font-semibold hover:bg-[#F1F4ED] transition-colors disabled:opacity-60"
            data-testid="stima-copy-save"
          >
            {busy ? "Salvo…" : "Salva personalizzazione"}
          </button>
        </div>
      )}
    </div>
  );
}

export default function EstimateValue() {
  const navigate = useNavigate();
  const [cities, setCities] = useState([]);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const reportRef = React.useRef(null);
  const [pdfExporting, setPdfExporting] = useState(false);
  const [agency, setAgency] = useState({});
  const [form, setForm] = useState({
    city: "",
    address: "",
    surface_sqm: "",
    state: "ottimo",
    zone: "semicentrale",
    property_type: "appartamento",
    floor: "",
    has_elevator: false,
    has_garage: false,
    has_terrace: false,
    // Advanced AVM fields
    energy_class: "",
    windows: "",
    exposure: "",
    view: "",
    year_built: "",
    rooms: "",
    // Iter 208 — dotazioni extra
    has_pool: false,
    has_garden: false,
    has_balcony: false,
    has_cellar: false,
    has_attic: false,
    has_double_bath: false,
    has_ac: false,
    has_security: false,
    heating: "",
    is_furnished: false,
  });
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  // Pronto-Stima funnel — agent's personal public link
  // iter v29 r103 — Preferisci slug nome-cognome (`/stima/gianfilippo-baio`)
  // al codice random (`/stima/KMCPZZR`). Più riconoscibile per il cliente,
  // più professionale come anteprima WhatsApp.
  const [stimaCode, setStimaCode] = useState("");
  const [stimaSlug, setStimaSlug] = useState("");
  const [stimaCount, setStimaCount] = useState(0);
  // iter v29 r235 — split link: stimaLink (mostrato UI, "umano") vs
  // stimaShareUrl (per WhatsApp/native share → attiva anteprima OG con
  // logo agente). Il share punta a /api/share/stima/{code} che fa redirect
  // umano e serve OG tags ai bot scraper.
  const stimaLink = (stimaSlug || stimaCode)
    ? `${window.location.origin}/stima/${stimaSlug || stimaCode}`
    : "";
  // iter v30 r144 — Link share corto `/api/s/{slug}` (era `/api/share/stima/`).
  const stimaShareUrl = (stimaSlug || stimaCode)
    ? `${window.location.origin}/api/s/${stimaSlug || stimaCode}`
    : "";

  useEffect(() => {
    api.get("/estimate/cities").then((r) => setCities(r.data.cities || [])).catch(() => {});
    api.get("/referrals/me").then((r) => setStimaCode(r.data?.code || "")).catch(() => {});
    api.get("/leads").then((r) => {
      const all = Array.isArray(r.data) ? r.data : (r.data?.leads || []);
      setStimaCount(all.filter((l) => l.source === "pronto_stima").length);
    }).catch(() => {});
    // Load agency profile for PDF report header
    api.get("/auth/me").then((r) => {
      const u = r.data || {};
      setAgency({
        name: u.agency_name || u.name || "",
        agent_name: u.name || "",
        phone: u.phone || u.agency_phone || "",
        email: u.email || "",
        logo_url: u.agency_logo_url || null,
      });
      // iter v29 r103 — Costruisce lo slug nome-cognome dal nome utente.
      // Lo usiamo come canonico nel link Stima al posto del codice random.
      const slug = (u.name || "")
        .normalize("NFKD").replace(/[\u0300-\u036f]/g, "")
        .toLowerCase().trim()
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/^-+|-+$/g, "");
      // iter v30 r144 — Single-word slug ora supportato (es. "alberto").
      if (slug && slug.length >= 3) setStimaSlug(slug);
    }).catch(() => {});
  }, []);

  const downloadPdf = async () => {
    if (!result || !reportRef.current) return;
    setPdfExporting(true);
    try {
      const cityClean = (form.city || "stima").toLowerCase().replace(/[^a-z0-9]+/g, "-");
      await exportSinglePageAsPdf({
        node: reportRef.current,
        filename: `pronto-stima-${cityClean}-${form.surface_sqm || "0"}mq.pdf`,
      });
      toast.success("PDF scaricato!");
    } catch (e) {
      toast.error("Errore nell'export PDF.");
    } finally {
      setPdfExporting(false);
    }
  };

  const copyStimaLink = async () => {
    if (!stimaLink) return;
    try {
      // iter v29 r242 — Copia SEMPRE l'URL share (/api/share/stima/), anche
      // se in UI mostriamo l'URL "umano" (/stima/). Il share URL attiva
      // l'anteprima OG personalizzata (logo + nome agente) quando incollato
      // in WhatsApp/Telegram/iMessage.
      await navigator.clipboard.writeText(stimaShareUrl || stimaLink);
      toast.success("Link copiato! Incollalo dove vuoi.");
    } catch {
      toast.error("Copia non riuscita");
    }
  };
  const shareStimaWA = () => {
    if (!stimaLink) return;
    // iter v28 r358 — User M1268: il copy WhatsApp deve trasmettere chiaramente
    // che NON si chiedono dati personali per vedere la stima (il funnel è
    // anonymous-first, vedi Stima.jsx). 30 secondi, niente registrazione.
    // iter v29 r240 — User M1396: tolto "privacy garantita", aggiunto copy
    // esplicito "Niente nome, niente numero di telefono, 100% anonimo".
    const txt = `Ciao, curioso di sapere quanto vale la tua casa? Te lo dico in 30 secondi. Niente nome, niente numero di telefono, 100% anonimo:\n${stimaShareUrl || stimaLink}`;
    window.open(`https://wa.me/?text=${encodeURIComponent(txt)}`, "_blank");
  };

  const set = (k) => (e) =>
    setForm((f) => ({
      ...f,
      [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value,
    }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.city || !form.surface_sqm) {
      toast.error("Inserisci almeno città e superficie");
      return;
    }
    setLoading(true);
    try {
      const payload = {
        city: form.city,
        address: form.address || null,
        surface_sqm: parseFloat(form.surface_sqm),
        state: form.state,
        zone: form.zone,
        property_type: form.property_type,
        floor: form.floor === "" ? null : parseInt(form.floor, 10),
        has_elevator: form.has_elevator,
        has_garage: form.has_garage,
        has_terrace: form.has_terrace,
        energy_class: form.energy_class || null,
        windows: form.windows || null,
        exposure: form.exposure || null,
        view: form.view || null,
        year_built: form.year_built === "" ? null : parseInt(form.year_built, 10),
        rooms: form.rooms === "" ? null : parseInt(form.rooms, 10),
        // Iter 208 — dotazioni extra
        has_pool: form.has_pool,
        has_garden: form.has_garden,
        has_balcony: form.has_balcony,
        has_cellar: form.has_cellar,
        has_attic: form.has_attic,
        has_double_bath: form.has_double_bath,
        has_ac: form.has_ac,
        has_security: form.has_security,
        heating: form.heating || null,
        is_furnished: form.is_furnished,
      };
      const { data } = await api.post("/estimate/value", payload);
      setResult(data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore stima");
    } finally {
      setLoading(false);
    }
  };

  const conf = result && (CONFIDENCE_BADGE[result.confidence] || CONFIDENCE_BADGE.bassa);

  return (
    <AppShell title="Calcolo stima" breadcrumb="Calcolo stima">
      <Link to="/dashboard" className="inline-flex items-center gap-1.5 text-sm text-[#6B6A65] hover:text-[#7C8B6E] mb-6">
        <ArrowLeft size={14} /> Dashboard
      </Link>

      {/* iter v28 r368 — User M1278: descrizioni dismissibili.
          Pulsante "?" per riaprire tutte le guide della sezione. */}
      <div className="flex items-center justify-end mb-1">
        <ReopenGuidesBtn
          keys={["calcolo-stima:intro", "calcolo-stima:disclaimer"]}
          testId="calcolo-stima-reopen-guides"
        />
      </div>

      <DismissibleIntro storageKey="calcolo-stima:intro">
        <div className="flex items-center gap-3 mb-2">
          <Calculator size={24} weight="regular" className="text-[#7C8B6E]" />
          <span className="text-[10px] uppercase tracking-[0.18em] text-[#7C8B6E] font-semibold">Calcolo stima · OMI + caratteristiche</span>
        </div>
        <h1 className="font-editorial text-4xl lg:text-5xl tracking-tight" data-testid="estimate-title">
          Quanto vale<br/>
          <span className="italic-accent text-[#7C8B6E]">questo immobile.</span>
        </h1>
        <p className="mt-3 text-[15px] text-[#6B6A65] max-w-xl">
          Stima OMI ufficiale, rifinita con i dati che inserisci. Più dati metti, più il range si stringe.
        </p>
      </DismissibleIntro>

      <DismissibleIntro storageKey="calcolo-stima:disclaimer">
        <div className="mt-5 max-w-xl p-3.5 bg-amber-50 border border-amber-200 rounded-md text-[12.5px] text-amber-900 leading-relaxed" data-testid="omi-disclaimer">
          <strong>Importante:</strong> stima indicativa, base di trattativa con il proprietario. Non sostituisce una perizia giurata.
        </div>
      </DismissibleIntro>

      <div className="mt-10 grid grid-cols-1 lg:grid-cols-2 gap-10">
        {/* FORM */}
        <form onSubmit={submit} className="space-y-5" data-testid="estimate-form">
          <div>
            <label className="overline block mb-2">Città *</label>
            <CityAutocomplete
              value={form.city}
              onChange={(v) => setForm((f) => ({ ...f, city: v }))}
              placeholder="Es. Milano"
              testId="estimate-city"
              required
            />
          </div>

          <div>
            <label className="overline block mb-2">Indirizzo</label>
            <input
              data-vista
              value={form.address}
              onChange={set("address")}
              placeholder="Es. Via Brera 12"
              data-testid="estimate-address"
            />
            <div className="text-[11px] text-[#9A968B] mt-1">
              Inserisci l{"'"}indirizzo completo → la zona (centro / semicentro / periferia) viene calcolata in automatico.
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="overline block mb-2">Superficie (m²) *</label>
              <input data-vista type="number" min="10" max="10000" step="1" value={form.surface_sqm} onChange={set("surface_sqm")} placeholder="95" data-testid="estimate-surface" required />
            </div>
            <div>
              <label className="overline block mb-2">Locali</label>
              <input data-vista type="number" min="1" max="20" step="1" value={form.rooms} onChange={set("rooms")} placeholder="3" data-testid="estimate-rooms" />
            </div>
          </div>

          <div>
            <label className="overline block mb-2">Tipologia</label>
            <select data-vista value={form.property_type} onChange={set("property_type")} data-testid="estimate-type" className="w-full">
              {TYPE_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="overline block mb-2">Stato</label>
              <select data-vista value={form.state} onChange={set("state")} data-testid="estimate-state" className="w-full">
                {STATE_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </div>
            <div>
              <label className="overline block mb-2">Zona</label>
              <select data-vista value={form.zone} onChange={set("zone")} data-testid="estimate-zone" className="w-full">
                {ZONE_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="overline block mb-2">Piano</label>
              <input data-vista type="number" min="-2" max="40" step="1" value={form.floor} onChange={set("floor")} placeholder="3" data-testid="estimate-floor" />
            </div>
            <div>
              <label className="overline block mb-2">Anno costruzione</label>
              <input data-vista type="number" min="1800" max="2030" step="1" value={form.year_built} onChange={set("year_built")} placeholder="1985" data-testid="estimate-year" />
            </div>
          </div>

          <div className="grid grid-cols-3 gap-2 pt-1">
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <input type="checkbox" checked={form.has_elevator} onChange={set("has_elevator")} data-testid="estimate-elevator" />
              Ascensore
            </label>
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <input type="checkbox" checked={form.has_garage} onChange={set("has_garage")} data-testid="estimate-garage" />
              Box auto
            </label>
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <input type="checkbox" checked={form.has_terrace} onChange={set("has_terrace")} data-testid="estimate-terrace" />
              Terrazzo
            </label>
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <input type="checkbox" checked={form.has_balcony} onChange={set("has_balcony")} data-testid="estimate-balcony" />
              Balcone
            </label>
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <input type="checkbox" checked={form.has_garden} onChange={set("has_garden")} data-testid="estimate-garden" />
              Giardino
            </label>
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <input type="checkbox" checked={form.has_pool} onChange={set("has_pool")} data-testid="estimate-pool" />
              Piscina
            </label>
          </div>

          {/* ADVANCED — collapsible */}
          <button
            type="button"
            onClick={() => setShowAdvanced((v) => !v)}
            className="text-xs uppercase tracking-[0.18em] text-[#7C8B6E] font-semibold inline-flex items-center gap-1.5 pt-2"
            data-testid="estimate-advanced-toggle"
          >
            <CaretDown size={12} weight="bold" className={showAdvanced ? "rotate-180 transition" : "transition"} />
            {showAdvanced ? "Nascondi caratteristiche avanzate" : "Aggiungi caratteristiche avanzate (per stima più precisa)"}
          </button>

          {showAdvanced && (
            <div className="space-y-4 border-l-2 border-[#7C8B6E]/30 pl-4" data-testid="estimate-advanced-section">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="overline block mb-2">Classe energetica</label>
                  <select data-vista value={form.energy_class} onChange={set("energy_class")} data-testid="estimate-energy" className="w-full">
                    {ENERGY_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                  </select>
                </div>
                <div>
                  <label className="overline block mb-2">Serramenti</label>
                  <select data-vista value={form.windows} onChange={set("windows")} data-testid="estimate-windows" className="w-full">
                    {WINDOWS_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                  </select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="overline block mb-2">Esposizione</label>
                  <select data-vista value={form.exposure} onChange={set("exposure")} data-testid="estimate-exposure" className="w-full">
                    {EXPOSURE_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                  </select>
                </div>
                <div>
                  <label className="overline block mb-2">Vista</label>
                  <select data-vista value={form.view} onChange={set("view")} data-testid="estimate-view" className="w-full">
                    {VIEW_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                  </select>
                </div>
              </div>

              {/* Iter 208 — Riscaldamento */}
              <div>
                <label className="overline block mb-2">Riscaldamento</label>
                <select data-vista value={form.heating} onChange={set("heating")} data-testid="estimate-heating" className="w-full">
                  <option value="">Non specificato</option>
                  <option value="autonomo">Autonomo</option>
                  <option value="centralizzato">Centralizzato</option>
                  <option value="pompa_calore">Pompa di calore</option>
                  <option value="nessuno">Assente</option>
                </select>
              </div>

              {/* Iter 208 — Altre dotazioni che muovono il prezzo */}
              <div>
                <label className="overline block mb-2">Altre dotazioni</label>
                <div className="grid grid-cols-2 gap-2">
                  <label className="flex items-center gap-2 text-sm cursor-pointer">
                    <input type="checkbox" checked={form.has_cellar} onChange={set("has_cellar")} data-testid="estimate-cellar" />
                    Cantina
                  </label>
                  <label className="flex items-center gap-2 text-sm cursor-pointer">
                    <input type="checkbox" checked={form.has_attic} onChange={set("has_attic")} data-testid="estimate-attic" />
                    Mansarda
                  </label>
                  <label className="flex items-center gap-2 text-sm cursor-pointer">
                    <input type="checkbox" checked={form.has_double_bath} onChange={set("has_double_bath")} data-testid="estimate-double-bath" />
                    Doppi servizi
                  </label>
                  <label className="flex items-center gap-2 text-sm cursor-pointer">
                    <input type="checkbox" checked={form.has_ac} onChange={set("has_ac")} data-testid="estimate-ac" />
                    Aria condizionata
                  </label>
                  <label className="flex items-center gap-2 text-sm cursor-pointer">
                    <input type="checkbox" checked={form.has_security} onChange={set("has_security")} data-testid="estimate-security" />
                    Portineria / Allarme
                  </label>
                  <label className="flex items-center gap-2 text-sm cursor-pointer">
                    <input type="checkbox" checked={form.is_furnished} onChange={set("is_furnished")} data-testid="estimate-furnished" />
                    Arredato
                  </label>
                </div>
              </div>

              <p className="text-[11px] text-[#6B6A65] leading-relaxed">
                Più caratteristiche compili, più la stima si stringe. Lascia {"\"non specificata\""}
                se non sei sicuro: il modello applica solo gli aggiustamenti di cui hai dato un valore.
              </p>
            </div>
          )}

          <button type="submit" disabled={loading} className="btn-primary w-full disabled:opacity-60" data-testid="estimate-submit">
            {loading ? "Calcolo..." : "Calcola valore stimato"}
          </button>
        </form>

        {/* RESULT */}
        <div>
          {!result ? (
            <div className="bg-[#F1F4ED] border border-[#7C8B6E]/20 rounded-sm p-6 text-sm text-[#6B6A65]">
              <Info size={16} weight="regular" className="text-[#7C8B6E] mb-2" />
              <p>
                Inserisci i dati e clicca <strong className="text-[#1C1C1A]">Calcola</strong>.
                Otterrai un range di prezzo basato sui valori OMI 2° semestre 2025 con aggiustamenti
                per stato, zona, piano, classe energetica, esposizione, vista, anno e dotazioni.
              </p>
              <p className="mt-3 text-xs">
                💡 <strong>Suggerimento:</strong> apri {"\"caratteristiche avanzate\""} per una stima più precisa.
              </p>
            </div>
          ) : (
            <div className="space-y-4" data-testid="estimate-result">
              {result.used_national_average && (
                <div className="bg-[#F8E5C8] border border-[#B8624A]/30 rounded-sm p-3 text-xs text-[#8A4530]" data-testid="estimate-fallback-warning">
                  Città non presente nel nostro dataset. Stima basata su media nazionale, range più ampio del solito.
                </div>
              )}
              {result.geocoded && result.geocoded.zone_hint && (
                <div className="bg-[#EDEFE8] border border-[#7C8B6E]/30 rounded-sm p-3 text-xs text-[#3A4A2E]" data-testid="estimate-geocoded-info">
                  <span className="font-semibold uppercase tracking-wider text-[10px]">Zona riconosciuta</span>
                  <div className="mt-1">
                    <strong className="capitalize">{result.geocoded.zone_hint}</strong>
                    {result.geocoded.suburb && <> · quartiere <strong>{result.geocoded.suburb}</strong></>}
                    {result.geocoded.distance_km != null && <> · {result.geocoded.distance_km} km dal centro storico</>}
                  </div>
                </div>
              )}

              <div className="bg-[#7C8B6E] text-white p-6 rounded-sm">
                <div className="flex items-start justify-between gap-3 mb-1">
                  <div className="text-[10px] uppercase tracking-[0.18em] opacity-80">Valore stimato</div>
                  {conf && (
                    <span className={`text-[10px] uppercase tracking-[0.14em] px-2 py-0.5 rounded-full border ${conf.cls}`} data-testid="estimate-confidence">
                      {conf.label}
                    </span>
                  )}
                </div>
                <div className="font-editorial text-4xl tracking-tight" data-testid="estimate-amount">
                  {fmtEUR(result.estimated)}
                </div>
                <div className="mt-3 text-sm opacity-90">
                  Range: <strong>{fmtEUR(result.range_min)}</strong> – <strong>{fmtEUR(result.range_max)}</strong>
                </div>
                <div className="mt-1 text-xs opacity-75">
                  €/m²: {fmtEUR(result.price_per_sqm)} ({fmtEUR(result.price_per_sqm_min)}–{fmtEUR(result.price_per_sqm_max)})
                </div>
              </div>

              <div className="border border-[#E5E7E0] rounded-sm p-4">
                <div className="text-[10px] uppercase tracking-[0.18em] text-[#6B6A65] font-semibold mb-3">Dettaglio calcolo</div>
                <dl className="text-sm space-y-1.5">
                  <div className="flex justify-between"><dt className="text-[#6B6A65]">Base OMI ({result.city_key})</dt><dd>{fmtEUR(result.city_omi_data.median)}/m²</dd></div>
                  <div className="flex justify-between"><dt className="text-[#6B6A65]">Stato</dt><dd>×{result.applied_multipliers.state}</dd></div>
                  <div className="flex justify-between"><dt className="text-[#6B6A65]">Zona</dt><dd>×{result.applied_multipliers.zone}</dd></div>
                  <div className="flex justify-between"><dt className="text-[#6B6A65]">Tipologia</dt><dd>×{result.applied_multipliers.type}</dd></div>
                  {result.applied_multipliers.floor_adj !== 0 && (
                    <div className="flex justify-between"><dt className="text-[#6B6A65]">Piano / ascensore</dt><dd>{pct(result.applied_multipliers.floor_adj)}</dd></div>
                  )}
                  {result.applied_multipliers.perks > 0 && (
                    <div className="flex justify-between"><dt className="text-[#6B6A65]">Dotazioni (box, terrazzo, piscina, giardino…)</dt><dd>{pct(result.applied_multipliers.perks)}</dd></div>
                  )}
                  {result.applied_multipliers.heating_adj !== undefined && result.applied_multipliers.heating_adj !== 0 && (
                    <div className="flex justify-between"><dt className="text-[#6B6A65]">Riscaldamento</dt><dd>{pct(result.applied_multipliers.heating_adj)}</dd></div>
                  )}
                  {result.applied_multipliers.energy_adj !== 0 && (
                    <div className="flex justify-between"><dt className="text-[#6B6A65]">Classe energetica</dt><dd>{pct(result.applied_multipliers.energy_adj)}</dd></div>
                  )}
                  {result.applied_multipliers.windows_adj !== 0 && (
                    <div className="flex justify-between"><dt className="text-[#6B6A65]">Serramenti</dt><dd>{pct(result.applied_multipliers.windows_adj)}</dd></div>
                  )}
                  {result.applied_multipliers.exposure_adj !== 0 && (
                    <div className="flex justify-between"><dt className="text-[#6B6A65]">Esposizione</dt><dd>{pct(result.applied_multipliers.exposure_adj)}</dd></div>
                  )}
                  {result.applied_multipliers.view_adj !== 0 && (
                    <div className="flex justify-between"><dt className="text-[#6B6A65]">Vista</dt><dd>{pct(result.applied_multipliers.view_adj)}</dd></div>
                  )}
                  {result.applied_multipliers.year_adj !== 0 && (
                    <div className="flex justify-between"><dt className="text-[#6B6A65]">Anno costruzione</dt><dd>{pct(result.applied_multipliers.year_adj)}</dd></div>
                  )}
                </dl>
              </div>

              <p className="text-xs text-[#6B6A65] leading-relaxed border-l-2 border-[#7C8B6E] pl-3">
                <strong className="text-[#1C1C1A]">Fonte:</strong> {result.source}<br/>
                {result.disclaimer}
              </p>

              <PortalCompare city={form.city} surface_sqm={form.surface_sqm} property_type={form.property_type} />

              {/* PDF download CTA */}
              <button
                type="button"
                onClick={downloadPdf}
                disabled={pdfExporting}
                data-testid="estimate-download-pdf"
                className="w-full mt-2 inline-flex items-center justify-center gap-2 bg-[#1C1C1A] hover:bg-[#3A3A36] text-white font-semibold py-3 rounded-lg transition-colors disabled:opacity-60 disabled:cursor-wait"
              >
                <FilePdf size={16} weight="duotone" />
                {pdfExporting ? "Genero il PDF…" : "Scarica report PDF (1 pagina A4)"}
              </button>
              <p className="text-[11px] text-[#9A968B] text-center -mt-1">
                Brandizzato con i tuoi dati agenzia. Mandalo al cliente prima del sopralluogo.
              </p>

              {/* iter 242c — disclaimer onesto su fonte dati */}
              <div className="mt-4 p-3 bg-[#F4F2EC] border-l-2 border-[#7C8B6E] rounded-r-sm text-[11px] text-[#5A584F] leading-relaxed">
                <strong className="text-[#1C1C1A]">Dati ufficiali OMI 2° semestre 2025</strong> · Osservatorio Mercato Immobiliare dell{"'"}Agenzia delle Entrate. Aggiornamento semestrale.
                <br/>
                Strumento di <strong>primo orientamento</strong>: per perizia formale rivolgersi a tecnico abilitato (geometra, architetto, perito).
              </div>
            </div>
          )}
        </div>
      </div>

      {/* ── Pronto-Stima funnel — link da condividere coi tuoi prospect ── */}
      {stimaCode && (
        <div className="mt-12 max-w-xl mx-auto bg-[#1C1C1A] text-white rounded-2xl p-5 sm:p-6" data-testid="pronto-stima-funnel-card">
          <div className="flex items-start gap-3">
            <div className="w-9 h-9 rounded-full bg-[#7C8B6E] flex items-center justify-center flex-shrink-0">
              <Sparkle size={18} weight="fill" className="text-white" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-[10px] uppercase tracking-[0.18em] text-[#A8B89A] font-bold">Strumento agente</div>
              <h3 className="font-editorial text-xl sm:text-2xl mt-1 leading-tight">
                Mandalo ai <em className="text-[#A8B89A]">proprietari</em> via WhatsApp.
              </h3>
              <p className="text-[13px] text-white/75 mt-2 leading-relaxed">
                Vedono il valore della loro casa in <strong className="text-white">30 secondi · senza dare nome o telefono</strong>.
                Se vogliono parlare con te, lasciano un contatto dopo aver visto la stima.
              </p>
            </div>
          </div>

          <div className="mt-4 bg-black/30 border border-white/10 rounded-lg px-3 py-2.5 flex items-center gap-2">
            <LinkIcon size={14} className="text-[#A8B89A] flex-shrink-0" />
            <code className="text-[12.5px] text-white/90 truncate flex-1" data-testid="pronto-stima-link">{stimaLink}</code>
          </div>

          <div className="mt-3 grid grid-cols-2 gap-2">
            <button
              type="button"
              onClick={copyStimaLink}
              className="flex items-center justify-center gap-1.5 bg-white text-[#1C1C1A] py-2.5 rounded-lg text-[13px] font-medium hover:bg-[#F1F4ED]"
              data-testid="copy-stima-link-btn"
            >
              <Copy size={14} weight="bold" /> Copia link
            </button>
            <button
              type="button"
              onClick={shareStimaWA}
              className="flex items-center justify-center gap-1.5 bg-[#25D366] text-white py-2.5 rounded-lg text-[13px] font-medium hover:opacity-90"
              data-testid="share-stima-wa-btn"
            >
              <WhatsappLogo size={14} weight="fill" /> WhatsApp
            </button>
          </div>

          {stimaCount > 0 && (
            <div className="mt-3 flex items-center gap-2 text-[12px] text-white/70">
              <Users size={13} />
              <span>{stimaCount} richies{stimaCount === 1 ? "ta" : "te"} ricevut{stimaCount === 1 ? "a" : "e"}</span>
            </div>
          )}

          {/* iter v28 r358 — Personalizzazione del testo del link Stima.
              L'agente può sovrascrivere headline, sottotitolo e testo del
              bottone CTA. Tutti opzionali → fallback ai default Pronto. */}
          <StimaCopyEditor />
        </div>
      )}

      {/* Off-screen render del report A4 — usato da exportSinglePageAsPdf.
          Posizionato fuori dalla viewport ma renderizzato (no display:none
          perché html2canvas non legge i nodi nascosti). */}
      {result && (
        <div style={{ position: "fixed", left: -10000, top: 0, pointerEvents: "none", zIndex: -1 }} aria-hidden="true">
          <EstimateReportPage
            ref={reportRef}
            result={result}
            form={form}
            agency={agency}
          />
        </div>
      )}
    </AppShell>
  );
}
